import { Controller } from "@hotwired/stimulus";
import {
  openRailsuiModal,
  closeRailsuiModal,
} from "./report_dashboard/report_dashboard_modal";

const POPULATE_ERROR_MESSAGE =
  "Unable to load students from the selected group. Try again.";
const POPULATE_EMPTY_GROUP_MESSAGE =
  "No students found for the selected group.";
const LOOKUP_ERROR_MESSAGE = "Unable to look up students by email. Try again.";
const UNKNOWN_EMAILS_MESSAGE =
  "These emails were not found or do not belong to students. Please, confirm they are rostered into your program or that you are using the correct email address.";
const REGISTER_ANYWAY_TEXT = "Register Anyway";
const SEARCH_DEBOUNCE_MS = 300;
const GROUP_FILTER_OPTIONS_FRAME_ID =
  "educator-home-students-group-filter-options";

export default class extends Controller {
  static targets = [
    "searchInput",
    "searchForm",
    "searchSelectedIdsField",
    "pageField",
    "studentRow",
    "checkbox",
    "selectAllCheckbox",
    "selectedIdsField",
    "selectedEmailsField",
    "customCourseIdsField",
    "assessmentDeliveryIdsField",
    "tabField",
    "selectedPanel",
    "selectedList",
    "selectedListItem",
    "selectedListItemTemplate",
    "selectedEmpty",
    "selectedCount",
    "tableFrame",
    "tableSkeletonTemplate",
    "paginationFrame",
    "paginationSkeletonTemplate",
    "resultsMount",
    "listViewSwitcher",
    "selectedGroups",
    "selectedPillItemTemplate",
    "applyButton",
  ];

  static values = {
    populateUrl: String,
    lookupEmailsUrl: String,
    searchUrl: String,
    selectedIds: { type: Array, default: [] },
    initialSelectedIds: { type: Array, default: [] },
    unknownEmails: { type: Array, default: [] },
    mainModalId: String,
    groupModalId: String,
    emailModalId: String,
    replaceTargetId: String,
    activeTab: String,
    listView: { type: String, default: "all" },
  };

  connect() {
    this.selectedIds = new Set((this.selectedIdsValue || []).map(String));
    this.initialSelectedIds = new Set(
      (this.initialSelectedIdsValue || []).map(String),
    );
    this.selectedStudents = new Map();
    this.unknownEmails = new Set(
      (this.unknownEmailsValue || []).map((email) =>
        String(email).toLowerCase(),
      ),
    );
    this.trackedGroupFilterCourseIds = [];
    this.trackedGroupFilterDeliveryIds = [];
    this.groupFilterRemoveInFlight = false;
    this.pendingUnknownEmailConfirm = false;
    this.pendingUnknownEmails = [];
    this.pendingEmailLookupEmails = [];
    this.pendingEmailLookupGrdus = [];
    this.hydrateSelectedStudentsFromList();
    this.portalHomes = new Map();
    this.portaledActionAbort = new AbortController();
    this.searchTimer = null;
    this.searchRequestId = 0;
    this.boundHandleTurboStreamRender = this.handleTurboStreamRender.bind(this);
    this.boundHandleTableFrameClick = this.handleTableFrameClick.bind(this);
    this.boundHandleTableFrameBeforeFetchResponse =
      this.handleTableFrameBeforeFetchResponse.bind(this);
    this.boundHandleTableFrameFetchError =
      this.handleTableFrameFetchError.bind(this);
    this.boundSyncListViewVisibility = this.syncListViewVisibility.bind(this);
    document.addEventListener(
      "turbo:before-stream-render",
      this.boundHandleTurboStreamRender,
    );
    window.addEventListener("resize", this.boundSyncListViewVisibility);
    if (this.hasTableFrameTarget) {
      this.tableFrameTarget.addEventListener(
        "click",
        this.boundHandleTableFrameClick,
      );
      this.tableFrameTarget.addEventListener(
        "turbo:before-fetch-response",
        this.boundHandleTableFrameBeforeFetchResponse,
      );
      this.tableFrameTarget.addEventListener(
        "turbo:fetch-request-error",
        this.boundHandleTableFrameFetchError,
      );
    }
    this.portalNestedModals();
    this.syncTrackedGroupFilterValues();
    this.syncCheckboxesFromSelection();
    this.updateSelectedIdsField();
    this.syncGroupSelectionFields();
    this.syncSearchSelectedIdsField();
    this.updateSelectAllState();
    this.updateSelectedList();
    this.syncListViewFromDom();
    this.syncListViewVisibility();
    this.updateSelectedGroupsPills();
    this.syncApplyButtonState();
  }

  disconnect() {
    document.removeEventListener(
      "turbo:before-stream-render",
      this.boundHandleTurboStreamRender,
    );
    window.removeEventListener("resize", this.boundSyncListViewVisibility);
    if (this.hasTableFrameTarget) {
      this.tableFrameTarget.removeEventListener(
        "click",
        this.boundHandleTableFrameClick,
      );
      this.tableFrameTarget.removeEventListener(
        "turbo:before-fetch-response",
        this.boundHandleTableFrameBeforeFetchResponse,
      );
      this.tableFrameTarget.removeEventListener(
        "turbo:fetch-request-error",
        this.boundHandleTableFrameFetchError,
      );
    }
    this.portaledActionAbort?.abort();
    this.clearSearchTimer();
    this.restoreNestedModals();
  }

  openMainModal(event) {
    event?.preventDefault();
    if (!this.mainModalIdValue) return;
    openRailsuiModal(this.application, this.mainModalIdValue, this.element);
    this.syncFilterButtonExpanded(true);
  }

  openGroupModal(event) {
    event?.preventDefault();
    event?.stopPropagation();
    if (!this.groupModalIdValue) return;
    this.clearGroupError();
    openRailsuiModal(this.application, this.groupModalIdValue, document);
    this.syncTrackedGroupFilterValues();
    this.refreshGroupFilterOptionsFrame();
  }

  openEmailModal(event) {
    event?.preventDefault();
    event?.stopPropagation();
    if (!this.emailModalIdValue) return;
    this.resetEmailModalUnknownState();
    openRailsuiModal(this.application, this.emailModalIdValue, document);
  }

  closeGroupModal() {
    if (!this.groupModalIdValue) return;
    closeRailsuiModal(this.application, this.groupModalIdValue, document);
  }

  closeEmailModal() {
    if (!this.emailModalIdValue) return;
    closeRailsuiModal(this.application, this.emailModalIdValue, document);
  }

  closeNestedModals() {
    this.closeEmailModal();
    this.closeGroupModal();
  }

  closeNestedModalsFromSelectedPanel() {
    this.closeNestedModals();
  }

  closeMainModal() {
    if (!this.mainModalIdValue) return;
    closeRailsuiModal(this.application, this.mainModalIdValue, this.element);
    this.syncFilterButtonExpanded(false);
  }

  syncBeforeSubmit() {
    this.syncActiveTabField();
    this.updateSelectedIdsField();
    this.syncGroupSelectionFields();
  }

  handleSubmitEnd(event) {
    if (event.detail?.success) {
      this.closeMainModal();
    }
  }

  searchChanged() {
    this.syncSearchSelectedIdsField();
    this.resetPageField();
    this.clearSearchTimer();
    this.searchRequestId += 1;
    const requestId = this.searchRequestId;
    this.showSearchLoading();
    this.searchTimer = window.setTimeout(
      () => this.submitSearchForm(requestId),
      SEARCH_DEBOUNCE_MS,
    );
  }

  submitSearchForm(requestId = this.searchRequestId) {
    if (requestId !== this.searchRequestId) return;

    if (this.hasSearchFormTarget && this.searchFormTarget.tagName === "FORM") {
      this.setSearchRequestIdField(requestId);
      this.searchFormTarget.requestSubmit();
      return;
    }

    this.navigateInlineSearch(requestId);
  }

  navigateInlineSearch(requestId) {
    if (!this.hasTableFrameTarget) return;

    const base =
      this.searchUrlValue ||
      this.searchFormTarget?.dataset?.searchUrl ||
      this.tableFrameTarget.getAttribute("src") ||
      this.tableFrameTarget.src;
    if (!base) return;

    const url = new URL(base, window.location.origin);
    const search = this.hasSearchInputTarget
      ? this.searchInputTarget.value.trim()
      : "";
    if (search) {
      url.searchParams.set("search", search);
    } else {
      url.searchParams.delete("search");
    }
    url.searchParams.set("page", "1");
    url.searchParams.set(
      "selected_student_ids",
      Array.from(this.selectedIds).join(","),
    );
    url.searchParams.set("_search_request_id", String(requestId));
    this.tableFrameTarget.src = `${url.pathname}${url.search}`;
  }

  setSearchRequestIdField(requestId) {
    if (!this.hasSearchFormTarget) return;

    let field = this.searchFormTarget.querySelector(
      "[name='_search_request_id']",
    );
    if (!field) {
      field = document.createElement("input");
      field.type = "hidden";
      field.name = "_search_request_id";
      this.searchFormTarget.appendChild(field);
    }
    field.value = String(requestId);
  }

  loadedSearchRequestId() {
    if (!this.hasTableFrameTarget) return null;

    const src =
      this.tableFrameTarget.getAttribute("src") ||
      this.tableFrameTarget.src ||
      "";
    if (!src) return null;

    const id = new URL(src, window.location.origin).searchParams.get(
      "_search_request_id",
    );
    if (!id) return null;

    return Number.parseInt(id, 10);
  }

  isStaleSearchRequest(loadedId = this.loadedSearchRequestId()) {
    return loadedId !== null && loadedId !== this.searchRequestId;
  }

  searchRequestIdFromUrl(url) {
    if (!url) return null;

    const id = new URL(url, window.location.origin).searchParams.get(
      "_search_request_id",
    );
    if (!id) return null;

    return Number.parseInt(id, 10);
  }

  handleTableFrameBeforeFetchResponse(event) {
    const responseUrl = event.detail?.fetchResponse?.response?.url;
    const loadedId = this.searchRequestIdFromUrl(responseUrl);
    if (loadedId !== null && loadedId !== this.searchRequestId) {
      event.preventDefault();
    }
  }

  handleTableFrameFetchError() {
    this.clearSearchLoading();
  }

  handleTableFrameClick(event) {
    const link = event.target.closest("a[data-turbo-frame]");
    if (!link || !this.hasSearchInputTarget || !this.hasTableFrameTarget)
      return;

    const url = new URL(link.href, window.location.origin);
    const currentSearch = this.searchInputTarget.value.trim();
    const linkSearch = (url.searchParams.get("search") || "").trim();
    if (currentSearch === linkSearch) return;

    event.preventDefault();
    this.clearSearchTimer();
    if (currentSearch) {
      url.searchParams.set("search", currentSearch);
    } else {
      url.searchParams.delete("search");
    }
    this.showSearchLoading();
    this.tableFrameTarget.src = `${url.pathname}${url.search}`;
  }

  tableFrameLoaded() {
    if (this.isStaleSearchRequest()) return;

    this.clearSearchLoading();
    this.syncCheckboxesFromSelection();
    this.updateSelectAllState();
    this.syncSearchSelectedIdsField();
    this.reloadPaginationFrame();
    this.syncListViewVisibility();
  }

  showSearchLoading() {
    this.paintSkeleton(
      this.hasTableFrameTarget && this.tableFrameTarget,
      this.hasTableSkeletonTemplateTarget && this.tableSkeletonTemplateTarget,
    );
    this.paintSkeleton(
      this.hasPaginationFrameTarget && this.paginationFrameTarget,
      this.hasPaginationSkeletonTemplateTarget &&
        this.paginationSkeletonTemplateTarget,
    );
  }

  clearSearchLoading() {
    if (this.hasTableFrameTarget)
      this.tableFrameTarget.setAttribute("aria-busy", "false");
  }

  paginationFrameLoaded() {
    if (this.hasPaginationFrameTarget)
      this.paginationFrameTarget.setAttribute("aria-busy", "false");
  }

  paintSkeleton(frame, template) {
    if (!frame || !template) return;

    frame.setAttribute("aria-busy", "true");
    frame.replaceChildren(template.content.cloneNode(true));
  }

  switchListView(event) {
    const input = event.target;
    if (!input?.matches?.("input.form-switcher-input")) return;

    this.closeNestedModals();
    this.listViewValue = input.value;
    this.syncListViewVisibility();
  }

  listViewValueChanged() {
    if (!this.isConnected) return;

    this.syncListViewVisibility();
  }

  isLargeViewport() {
    if (typeof window.matchMedia !== "function") return false;

    return window.matchMedia("(min-width: 1024px)").matches;
  }

  syncListViewFromDom() {
    if (!this.hasListViewSwitcherTarget) return;

    const checked = this.listViewSwitcherTarget.querySelector(
      "input.form-switcher-input:checked",
    );
    if (checked) this.listViewValue = checked.value;
  }

  syncListViewVisibility() {
    const large = this.isLargeViewport();
    const showAll = this.listViewValue === "all";

    if (this.hasResultsMountTarget) {
      this.resultsMountTarget.hidden = !large && !showAll;
    }

    if (this.hasSelectedPanelTarget) {
      const panel = this.selectedPanelTarget;
      if (large) {
        panel.classList.remove("max-lg:hidden", "max-lg:flex");
      } else if (showAll) {
        panel.classList.add("max-lg:hidden");
        panel.classList.remove("max-lg:flex");
      } else {
        panel.classList.remove("max-lg:hidden");
        panel.classList.add("max-lg:flex");
      }
    }

    if (this.hasPaginationFrameTarget) {
      this.paginationFrameTarget.hidden = !large && !showAll;
    }
  }

  studentsTableUrl() {
    if (!this.hasSearchFormTarget) return null;

    const url = new URL(this.searchFormTarget.action, window.location.origin);
    new FormData(this.searchFormTarget).forEach((value, key) => {
      if (String(value).length > 0) url.searchParams.set(key, value);
    });
    return url.toString();
  }

  reloadPaginationFrame() {
    if (!this.hasPaginationFrameTarget || !this.hasTableFrameTarget) return;

    const src =
      this.tableFrameTarget.getAttribute("src") || this.studentsTableUrl();
    if (!src) return;

    const url = new URL(src, window.location.origin);
    url.searchParams.set("_frame", "pagination");
    this.paginationFrameTarget.src = `${url.pathname}${url.search}`;
    this.paginationFrameTarget.reload();
  }

  filterStudents() {
    this.searchChanged();
  }

  toggleStudent(event) {
    const checkbox = event.currentTarget;
    const id = String(checkbox.value);
    const row = checkbox.closest("[data-students-modal-target='studentRow']");

    if (checkbox.checked) {
      this.selectedIds.add(id);
      if (row) this.rememberStudentFromRow(row);
    } else {
      this.selectedIds.delete(id);
      this.selectedStudents.delete(id);
    }

    this.updateSelectedIdsField();
    this.syncCheckboxesFromSelection();
    this.updateSelectAllState();
    this.updateSelectedList();
    if (!checkbox.checked) {
      void this.reconcileGroupFiltersAfterStudentRemoval();
    }
  }

  toggleSelectAll(event) {
    const checked = event.currentTarget.checked;
    const visibleRows = this.visibleStudentRows();

    visibleRows.forEach((row) => {
      const id = String(row.dataset.studentId);
      if (checked) {
        this.selectedIds.add(id);
        this.rememberStudentFromRow(row);
      } else {
        this.selectedIds.delete(id);
        this.selectedStudents.delete(id);
      }
    });

    this.updateSelectedIdsField();
    this.syncCheckboxesFromSelection();
    this.updateSelectAllState();
    this.updateSelectedList();
  }

  async removeSelectedStudent(event) {
    event.preventDefault();
    const id = String(event.currentTarget.dataset.studentId || "");
    if (!id) return;

    const list = this.hasSelectedListTarget ? this.selectedListTarget : null;
    const removeButtons = list
      ? Array.from(list.querySelectorAll(".dropdown-selected-list-remove"))
      : [];
    const removedIndex = removeButtons.findIndex(
      (button) => String(button.dataset.studentId) === id,
    );

    this.selectedIds.delete(id);
    this.selectedStudents.delete(id);
    this.updateSelectedIdsField();
    this.syncCheckboxesFromSelection();
    this.syncSearchSelectedIdsField();
    this.updateSelectAllState();
    this.updateSelectedList();

    const nextRemoveButtons = list
      ? Array.from(list.querySelectorAll(".dropdown-selected-list-remove"))
      : [];
    const nextRemove =
      removedIndex >= 0
        ? nextRemoveButtons[removedIndex]
        : nextRemoveButtons[0];
    if (nextRemove) {
      nextRemove.focus();
    } else if (this.hasSearchInputTarget) {
      this.searchInputTarget.focus();
    } else if (this.hasSelectAllCheckboxTarget) {
      this.selectAllCheckboxTarget.focus();
    }

    return this.reconcileGroupFiltersAfterStudentRemoval();
  }

  async applyGroupSelection(event) {
    event.preventDefault();
    if (!this.populateUrlValue) return;

    this.clearGroupError();

    const { customCourses, assessmentDeliveries } =
      this.readGroupFilterValues();
    const filtersEmpty =
      customCourses.length === 0 && assessmentDeliveries.length === 0;

    if (filtersEmpty) {
      this.syncGroupSelectionFields();
      this.syncSearchSelectedIdsField();
      this.syncTrackedGroupFilterValues();
      this.clearGroupError();
      this.updateSelectedGroupsPills({ fromApply: true });
      this.closeGroupModal();
      return;
    }

    try {
      const grdus = await this.fetchPopulateGrdus({
        custom_courses: customCourses,
        assessment_deliveries: assessmentDeliveries,
      });

      if (grdus === null) {
        this.showGroupError(POPULATE_ERROR_MESSAGE);
        return;
      }

      if (grdus.length === 0) {
        this.showGroupError(POPULATE_EMPTY_GROUP_MESSAGE);
        return;
      }

      grdus.forEach((grdu) => this.rememberStudent(grdu));
      this.syncCheckboxesFromSelection();
      this.updateSelectedIdsField();
      this.syncSearchSelectedIdsField();
      this.updateSelectAllState();
      this.updateSelectedList();
      this.syncGroupSelectionFields();
      this.syncTrackedGroupFilterValues();
      this.clearGroupError();
      this.updateSelectedGroupsPills({ fromApply: true });
      this.closeGroupModal();
    } catch (_error) {
      this.showGroupError(POPULATE_ERROR_MESSAGE);
    }
  }

  async groupFilterDropdownChanged() {
    if (this.groupFilterRemoveInFlight) return;

    const { customCourses, assessmentDeliveries } =
      this.readGroupFilterValues();
    const previousCourses = this.trackedGroupFilterCourseIds || [];
    const previousDeliveries = this.trackedGroupFilterDeliveryIds || [];
    const removedCourses = previousCourses.filter(
      (id) => !customCourses.includes(id),
    );
    const removedDeliveries = previousDeliveries.filter(
      (id) => !assessmentDeliveries.includes(id),
    );

    if (removedCourses.length === 0 && removedDeliveries.length === 0) {
      this.syncTrackedGroupFilterValues();
      this.syncGroupSelectionFields();
      this.updateSelectedGroupsPills({ fromApply: true });
      return;
    }

    this.groupFilterRemoveInFlight = true;

    try {
      const snapshot = this.snapshotGroupFilterRemovalState();

      const reconciled = await this.reconcileStudentsAfterGroupFilterRemoval(
        removedCourses,
        removedDeliveries,
        customCourses,
        assessmentDeliveries,
      );

      if (!reconciled) {
        this.restoreGroupFilterRemovalState(snapshot);
        this.restoreGroupFilterDropdownFromTracked();
        this.showGroupError(POPULATE_ERROR_MESSAGE);
        return;
      }

      this.trackedGroupFilterCourseIds = customCourses;
      this.trackedGroupFilterDeliveryIds = assessmentDeliveries;
      if (this.hasCustomCourseIdsFieldTarget) {
        this.customCourseIdsFieldTarget.value = customCourses.join(",");
      }
      if (this.hasAssessmentDeliveryIdsFieldTarget) {
        this.assessmentDeliveryIdsFieldTarget.value =
          assessmentDeliveries.join(",");
      }
      this.writeGroupFilterValue("customCoursesDropdown", customCourses);
      this.writeGroupFilterValue("deliveriesDropdown", assessmentDeliveries);
      this.updateSelectedGroupsPills({ fromApply: true });
    } finally {
      this.groupFilterRemoveInFlight = false;
    }
  }

  async applyEmailList(event) {
    event.preventDefault();

    const emails = this.readEmailInputList();
    if (emails.length === 0) return;

    if (this.pendingUnknownEmailConfirm) {
      if (this.sameEmailList(emails, this.pendingEmailLookupEmails)) {
        this.confirmUnknownEmails();
        return;
      }
      this.resetEmailModalUnknownState();
    }

    if (!this.lookupEmailsUrlValue) {
      this.applyEmailListFromVisibleRows(emails);
      return;
    }

    try {
      const response = await fetch(this.lookupEmailsUrlValue, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          "X-CSRF-Token": this.csrfToken(),
        },
        body: JSON.stringify({ emails }),
      });

      if (!response.ok) {
        this.showEmailError(LOOKUP_ERROR_MESSAGE);
        return;
      }

      let data;
      try {
        data = await response.json();
      } catch (_error) {
        this.showEmailError(LOOKUP_ERROR_MESSAGE);
        return;
      }

      const unknown = (data.unknown_emails || []).map((email) =>
        String(email).toLowerCase(),
      );
      this.finishEmailListApply(unknown, {
        emails,
        grdus: data.grdus || [],
      });
    } catch (_error) {
      this.showEmailError(LOOKUP_ERROR_MESSAGE);
    }
  }

  applyEmailListFromVisibleRows(emails) {
    const grdus = [];
    const unknown = [];

    emails.forEach((email) => {
      const row = this.studentRowTargets.find(
        (candidate) => this.rowEmail(candidate).toLowerCase() === email,
      );
      if (row) {
        const { firstName, lastName } = this.rowName(row);
        grdus.push({
          id: row.dataset.studentId,
          email: this.rowEmail(row),
          first_name: firstName,
          last_name: lastName,
          user_id: row.dataset.userId || "",
        });
      } else {
        unknown.push(email);
      }
    });

    this.finishEmailListApply(unknown, { emails, grdus });
  }

  finishEmailListApply(unknown, { emails = [], grdus = [] } = {}) {
    const pending = Array.from(unknown || []).map((email) =>
      String(email).toLowerCase(),
    );
    this.pendingEmailLookupEmails = Array.from(emails || []).map((email) =>
      String(email).toLowerCase(),
    );
    this.pendingEmailLookupGrdus = Array.from(grdus || []);
    this.showUnknownEmails(pending);
    if (pending.length > 0) {
      this.pendingUnknownEmails = pending;
      this.pendingUnknownEmailConfirm = true;
      this.setEmailApplyRegisterAnyway(true);
      return;
    }

    this.commitPendingEmailLookup();
    this.resetEmailModalUnknownState();
    this.closeEmailModal();
  }

  confirmUnknownEmails() {
    this.commitPendingEmailLookup();
    (this.pendingUnknownEmails || []).forEach((email) =>
      this.unknownEmails.add(email),
    );
    this.updateSelectedList();
    this.updateSelectedGroupsPills();
    this.syncSelectedEmailsField();
    this.syncCourseBuilderRemoveFields();
    this.resetEmailModalUnknownState();
    this.closeEmailModal();
  }

  commitPendingEmailLookup() {
    (this.pendingEmailLookupGrdus || []).forEach((grdu) =>
      this.rememberStudent(grdu),
    );
    this.pendingEmailLookupGrdus = [];
    this.pendingEmailLookupEmails = [];
    this.syncCheckboxesFromSelection();
    this.updateSelectedIdsField();
    this.syncSearchSelectedIdsField();
    this.updateSelectAllState();
    this.updateSelectedList();
    this.updateSelectedGroupsPills();
    this.syncSelectedEmailsField();
    this.syncCourseBuilderRemoveFields();
  }

  resetEmailModalUnknownState() {
    this.pendingUnknownEmailConfirm = false;
    this.pendingUnknownEmails = [];
    this.pendingEmailLookupEmails = [];
    this.pendingEmailLookupGrdus = [];
    this.setEmailApplyRegisterAnyway(false);
    this.clearUnknownEmailsBanner();
  }

  readEmailInputList() {
    const emailInput = this.nestedField("emailInput");
    if (!emailInput) return [];

    return emailInput.value
      .split(/[,\n]/)
      .map((email) => email.trim().toLowerCase())
      .filter(Boolean);
  }

  sameEmailList(left, right) {
    const normalize = (list) =>
      Array.from(new Set((list || []).map((email) => String(email).toLowerCase()))).sort();
    const a = normalize(left);
    const b = normalize(right);
    return a.length === b.length && a.every((email, index) => email === b[index]);
  }

  handleTurboStreamRender(event) {
    const stream = event.target;
    if (stream?.getAttribute?.("target") !== this.replaceTargetId()) return;

    const defaultRender = event.detail.render;
    event.detail.render = async (streamElement) => {
      await defaultRender(streamElement);
      this.resetNestedModalsAfterStreamReplace();
    };
  }

  resetNestedModalsAfterStreamReplace() {
    this.discardPortaledModals();
    this.portaledActionAbort?.abort();
    this.portaledActionAbort = new AbortController();
    this.portalNestedModals();
    this.reloadSelectionFromDom();
  }

  reloadSelectionFromDom() {
    const field = this.element.querySelector(
      "[data-students-modal-target='selectedIdsField']",
    );
    const ids = (field?.value || "")
      .split(",")
      .map((value) => value.trim())
      .filter(Boolean);

    this.selectedIds = new Set(ids.map(String));
    Array.from(this.selectedStudents.keys()).forEach((id) => {
      if (!this.selectedIds.has(id)) this.selectedStudents.delete(id);
    });
    this.hydrateSelectedStudentsFromList();
    this.hydrateUnknownEmailsFromDom();
    this.syncCheckboxesFromSelection();
    this.updateSelectedIdsField();
    this.syncGroupSelectionFields();
    this.syncSearchSelectedIdsField();
    this.updateSelectAllState();
    this.updateSelectedList();

    this.syncTrackedGroupFilterValues();
    this.updateSelectedGroupsPills();
  }

  hydrateUnknownEmailsFromDom() {
    if (!this.unknownEmails) this.unknownEmails = new Set();

    const root = this.hasSelectedGroupsTarget
      ? this.selectedGroupsTarget
      : this.element;

    root.querySelectorAll(".students-modal__email-pill-remove").forEach((button) => {
      const email = button.dataset.email?.trim().toLowerCase();
      if (email) this.unknownEmails.add(email);
    });

    if (this.hasSelectedListTarget) {
      this.selectedListTarget
        .querySelectorAll(".students-modal__selected-email--unknown")
        .forEach((label) => {
          const email = label.textContent?.trim().toLowerCase();
          if (email) this.unknownEmails.add(email);
        });
    }
  }

  syncCheckboxesFromSelection() {
    this.checkboxTargets.forEach((checkbox) => {
      checkbox.checked = this.selectedIds.has(String(checkbox.value));
    });
  }

  updateSelectedIdsField() {
    if (this.hasSelectedIdsFieldTarget) {
      this.selectedIdsFieldTarget.value = Array.from(this.selectedIds).join(
        ",",
      );
    }
    this.syncSelectedEmailsField();
    this.syncCourseBuilderRemoveFields();
    this.syncFilterButtonActive();
    this.syncSearchSelectedIdsField();
    this.syncApplyButtonState();
  }

  syncApplyButtonState() {
    if (!this.hasApplyButtonTarget) return;

    this.setButtonDisabled(this.applyButtonTarget, this.selectedIds.size === 0);
  }

  syncEmailApplyButtonState() {
    const button = this.nestedField("emailApplyButton");
    const input = this.nestedField("emailInput");
    if (!button || !input) return;

    this.setButtonDisabled(button, input.value.trim() === "");
  }

  setButtonDisabled(button, disabled) {
    button.disabled = disabled;
    button.setAttribute("aria-disabled", disabled ? "true" : "false");
  }

  syncSelectedEmailsField() {
    if (!this.hasSelectedEmailsFieldTarget) return;

    const emails = Array.from(this.selectedIds)
      .map((id) => this.selectedStudents.get(id)?.email)
      .filter(Boolean)
      .map((email) => String(email).toLowerCase());
    const unknown = this.sortedUnknownEmails();
    this.selectedEmailsFieldTarget.value = [
      ...new Set([...emails, ...unknown]),
    ].join(",");
  }

  syncCourseBuilderRemoveFields() {
    const removeStudents = document.getElementById("remove-students-container");
    const removeUnrostered = document.getElementById(
      "remove-unrostered-students-container",
    );
    if (!removeStudents && !removeUnrostered) return;

    if (removeStudents) {
      removeStudents.replaceChildren();
      Array.from(this.initialSelectedIds || []).forEach((id) => {
        if (this.selectedIds.has(id)) return;
        const userId =
          this.initialStudentUserIds?.get(id) ||
          this.selectedStudents.get(id)?.userId;
        if (!userId) return;
        const input = document.createElement("input");
        input.type = "hidden";
        input.name = "remove_students[]";
        input.value = String(userId);
        removeStudents.appendChild(input);
      });
    }

    if (removeUnrostered) {
      const initialUnknown = new Set(
        (this.unknownEmailsValue || []).map((email) =>
          String(email).toLowerCase(),
        ),
      );
      removeUnrostered.replaceChildren();
      initialUnknown.forEach((email) => {
        if (this.unknownEmails.has(email)) return;
        const input = document.createElement("input");
        input.type = "hidden";
        input.name = "remove_unrostered_students[]";
        input.value = email;
        removeUnrostered.appendChild(input);
      });
    }
  }

  syncSearchSelectedIdsField() {
    if (!this.hasSearchSelectedIdsFieldTarget) return;
    this.searchSelectedIdsFieldTarget.value = Array.from(this.selectedIds).join(
      ",",
    );
  }

  syncGroupSelectionFields() {
    const coursesWrapper = this.nestedField("customCoursesDropdown");
    if (this.hasCustomCourseIdsFieldTarget && coursesWrapper) {
      this.customCourseIdsFieldTarget.value =
        this.readDropdownValues(coursesWrapper).join(",");
    }

    const deliveriesWrapper = this.nestedField("deliveriesDropdown");
    if (this.hasAssessmentDeliveryIdsFieldTarget && deliveriesWrapper) {
      this.assessmentDeliveryIdsFieldTarget.value =
        this.readDropdownValues(deliveriesWrapper).join(",");
    }
  }

  syncActiveTabField() {
    if (!this.hasTabFieldTarget) return;

    const activeTab =
      this.element.dataset.studentsModalActiveTab || this.activeTabValue;
    if (activeTab) this.tabFieldTarget.value = activeTab;
  }

  syncFilterButtonActive() {
    const buttons = this.element.querySelectorAll(
      "[data-students-modal-filter]",
    );
    const active = this.selectedIds.size > 0;

    buttons.forEach((button) => {
      button.classList.toggle("is-active", active);
      button.setAttribute("aria-pressed", String(active));
      button.dataset.studentsModalFilterActive = String(active);
    });
  }

  syncFilterButtonExpanded(expanded) {
    this.element
      .querySelectorAll("[data-students-modal-filter]")
      .forEach((button) => {
        button.setAttribute("aria-expanded", String(expanded));
      });
  }

  updateSelectAllState() {
    if (!this.hasSelectAllCheckboxTarget) return;

    const visibleRows = this.visibleStudentRows();
    const visibleIds = visibleRows.map((row) => String(row.dataset.studentId));
    const selectedVisibleCount = visibleIds.filter((id) =>
      this.selectedIds.has(id),
    ).length;

    const checkbox = this.selectAllCheckboxTarget;
    if (visibleIds.length === 0) {
      checkbox.checked = false;
      checkbox.indeterminate = false;
      return;
    }

    checkbox.checked = selectedVisibleCount === visibleIds.length;
    checkbox.indeterminate =
      selectedVisibleCount > 0 && selectedVisibleCount < visibleIds.length;
  }

  updateSelectedList() {
    if (!this.hasSelectedListTarget) return;

    const students = Array.from(this.selectedIds)
      .map((id) => this.selectedStudents.get(id))
      .filter(Boolean)
      .sort((left, right) => left.email.localeCompare(right.email));
    const unknownEmails = this.sortedUnknownEmails();

    const count = String(students.length + unknownEmails.length);
    this.selectedCountTargets.forEach((target) => {
      target.textContent = count;
    });
    this.selectedListTarget.replaceChildren();

    unknownEmails.forEach((email) => {
      this.selectedListTarget.appendChild(
        this.buildSelectedUnknownListItem(email),
      );
    });
    students.forEach((student) => {
      this.selectedListTarget.appendChild(this.buildSelectedListItem(student));
    });

    this.selectedListTarget.hidden =
      students.length === 0 && unknownEmails.length === 0;
    if (this.hasSelectedEmptyTarget) {
      this.selectedEmptyTarget.hidden =
        students.length > 0 || unknownEmails.length > 0;
    }

    if (this.hasSelectedPanelTarget) {
      const skeleton = this.selectedPanelTarget.querySelector(
        ".students-modal__selected-list-skeleton",
      );
      if (skeleton) skeleton.hidden = true;
    }
  }

  updateSelectedGroupsPills({ fromApply = false } = {}) {
    if (!this.hasSelectedGroupsTarget) return;

    const dropdownGroups = this.readGroupFilterItems();
    const groups = this.resolveGroupPillItems(dropdownGroups, { fromApply });
    // Unknown emails render in Selected Students (Figma 8763:46104), not as pills.
    const emails = this.hasSelectedListTarget
      ? []
      : this.sortedUnknownEmails();
    const container = this.selectedGroupsTarget;

    if (groups.length === 0 && fromApply && this.hasStoredGroupFilterValues()) {
      this.syncSelectedGroupPillsFromFields();
      return;
    }

    if (groups.length === 0 && emails.length === 0) {
      if (fromApply || !this.hasAppliedGroupPillSelections()) {
        container.replaceChildren();
        container.hidden = true;
      }
      return;
    }

    const list = this.createSelectedGroupsList();

    groups.forEach((group) => {
      list.appendChild(this.buildSelectedGroupPillItem(group));
    });
    emails.forEach((email) => {
      list.appendChild(this.buildSelectedEmailPillItem(email));
    });

    container.replaceChildren(list);
    container.hidden = false;
  }

  createSelectedGroupsList() {
    const list = document.createElement("ul");
    list.className = "students-modal__selected-groups-list";
    list.setAttribute("role", "list");
    return list;
  }

  resolveGroupPillItems(
    dropdownGroups = this.readGroupFilterItems(),
    { fromApply = false } = {},
  ) {
    if (!fromApply) {
      return this.readRenderedGroupPillItems();
    }

    const applied = this.readAppliedGroupFilterValues();
    const allowedCourseIds = new Set(applied.customCourses.map(String));
    const allowedDeliveryIds = new Set(
      applied.assessmentDeliveries.map(String),
    );
    const allowed = (group) =>
      (group.type === "custom_course" &&
        allowedCourseIds.has(String(group.value))) ||
      (group.type === "assessment_delivery" &&
        allowedDeliveryIds.has(String(group.value)));

    const fromDropdown = dropdownGroups.filter(allowed);
    if (fromDropdown.length > 0) return fromDropdown;

    const fromRendered = this.readRenderedGroupPillItems().filter(allowed);
    if (fromRendered.length > 0) return fromRendered;

    return [];
  }

  readRenderedGroupPillItems() {
    if (!this.hasSelectedGroupsTarget) return [];

    const items = [];

    this.selectedGroupsTarget
      .querySelectorAll(".students-modal__group-pill")
      .forEach((chip) => {
        const button = chip.querySelector(".students-modal__group-pill-remove");
        if (!button) return;

        const label = chip.querySelector("span")?.textContent?.trim();
        const type = button.dataset.groupType;
        const value = String(button.dataset.groupId || "").trim();
        const tone = chip.classList.contains("badge-aq-chip--pill-green")
          ? "green"
          : "blue";

        if (label && value && type) {
          items.push({ label, value, type, tone });
        }
      });

    return items;
  }

  syncSelectedGroupPillsFromFields() {
    if (!this.hasSelectedGroupsTarget) return;

    const { customCourses, assessmentDeliveries } =
      this.readAppliedGroupFilterValues();
    const container = this.selectedGroupsTarget;
    const list = container.querySelector(
      ".students-modal__selected-groups-list",
    );

    if (!list) {
      if (this.sortedUnknownEmails().length === 0) {
        container.hidden = true;
        return;
      }

      const emailList = this.createSelectedGroupsList();
      container.replaceChildren(emailList);
      this.syncEmailPillsInList(emailList);
      container.hidden = false;
      return;
    }

    const appliedCourseIds = new Set(customCourses.map(String));
    const appliedDeliveryIds = new Set(assessmentDeliveries.map(String));

    list.querySelectorAll(".badge-aq-list-item").forEach((item) => {
      const button = item.querySelector(".students-modal__group-pill-remove");
      if (!button) return;

      const type = button.dataset.groupType;
      const id = String(button.dataset.groupId || "");
      const keep =
        (type === "custom_course" && appliedCourseIds.has(id)) ||
        (type === "assessment_delivery" && appliedDeliveryIds.has(id));

      if (!keep) item.remove();
    });

    this.syncEmailPillsInList(list);

    if (list.children.length === 0) {
      container.replaceChildren();
      container.hidden = true;
      return;
    }

    container.hidden = false;
  }

  sortedUnknownEmails() {
    return Array.from(this.unknownEmails || []).sort((left, right) =>
      left.localeCompare(right),
    );
  }

  syncEmailPillsInList(list) {
    if (!list) return;

    list
      .querySelectorAll("[data-students-modal-email-pill]")
      .forEach((item) => {
        item.remove();
      });

    this.sortedUnknownEmails().forEach((email) => {
      list.appendChild(this.buildSelectedEmailPillItem(email));
    });
  }

  buildSelectedGroupPillItem(group) {
    return this.buildSelectedPillItem({
      label: group.label,
      tone: group.tone,
      pillClass: "students-modal__group-pill",
      removeClass: "students-modal__group-pill-remove",
      removeAction: "click->students-modal#removeSelectedGroup",
      dataset: {
        groupType: group.type,
        groupId: group.value,
      },
    });
  }

  buildSelectedEmailPillItem(email) {
    return this.buildSelectedPillItem({
      label: email,
      tone: "orange",
      pillClass: "students-modal__email-pill",
      removeClass: "students-modal__email-pill-remove",
      removeAction: "click->students-modal#removeSelectedEmail",
      dataset: { email },
      emailPill: true,
    });
  }

  buildSelectedPillItem({
    label,
    tone,
    pillClass,
    removeClass,
    removeAction,
    dataset = {},
    emailPill = false,
  }) {
    const item =
      this.selectedPillItemTemplateTarget.content.firstElementChild.cloneNode(
        true,
      );

    if (emailPill) {
      item.dataset.studentsModalEmailPill = "true";
    } else {
      delete item.dataset.studentsModalEmailPill;
    }

    const chip = item.querySelector(".students-modal__selected-pill");
    if (chip) {
      chip.className = `badge-aq-chip badge-aq-chip--pill-${tone} students-modal__selected-pill ${pillClass}`;
    }

    const text = chip?.querySelector(":scope > span");
    if (text) text.textContent = label;

    const button = item.querySelector(".students-modal__selected-pill-remove");
    if (button) {
      button.className = `students-modal__selected-pill-remove ${removeClass}`;
      button.setAttribute("aria-label", `Remove ${label}`);
      button.dataset.action = removeAction;
      Object.entries(dataset).forEach(([key, value]) => {
        button.dataset[key] = value;
      });
    }

    return item;
  }

  removeSelectedEmail(event) {
    event.preventDefault();
    event.stopPropagation();

    const email = String(event.currentTarget.dataset.email || "").toLowerCase();
    if (!email) return;

    const list = this.hasSelectedGroupsTarget
      ? this.selectedGroupsTarget.querySelector(
          ".students-modal__selected-groups-list",
        )
      : null;
    const removeButtons = list
      ? Array.from(list.querySelectorAll(".students-modal__email-pill-remove"))
      : [];
    const removedIndex = removeButtons.findIndex(
      (button) => String(button.dataset.email || "").toLowerCase() === email,
    );

    this.unknownEmails.delete(email);
    this.updateSelectedList();
    this.updateSelectedGroupsPills({ fromApply: true });
    this.showUnknownEmails(this.sortedUnknownEmails());
    this.syncSelectedEmailsField();
    this.syncCourseBuilderRemoveFields();

    if (this.hasSelectedListTarget) {
      const nextUnknownRemove = this.selectedListTarget.querySelector(
        `.dropdown-selected-list-remove[data-email]`,
      );
      if (nextUnknownRemove?.isConnected) {
        nextUnknownRemove.focus();
        return;
      }
    }

    const nextList = this.hasSelectedGroupsTarget
      ? this.selectedGroupsTarget.querySelector(
          ".students-modal__selected-groups-list",
        )
      : null;
    const nextRemoveButtons = nextList
      ? Array.from(
          nextList.querySelectorAll(".students-modal__email-pill-remove"),
        )
      : [];
    const nextRemove =
      removedIndex >= 0
        ? nextRemoveButtons[removedIndex] ||
          nextRemoveButtons[removedIndex - 1] ||
          nextRemoveButtons[0]
        : nextRemoveButtons[0];

    if (nextRemove?.isConnected) {
      nextRemove.focus();
    }
  }

  async removeSelectedGroup(event) {
    event.preventDefault();
    event.stopPropagation();

    if (this.groupFilterRemoveInFlight) return;

    const button = event.currentTarget;
    const groupType = button.dataset.groupType;
    const groupId = String(button.dataset.groupId || "");
    if (!groupType || !groupId) return;

    const removedPillIndex = this.groupPillRemoveButtons().indexOf(button);

    this.groupFilterRemoveInFlight = true;

    try {
      const snapshot = this.snapshotGroupFilterRemovalState();

      const applied = this.readAppliedGroupFilterValues();
      let courses = applied.customCourses;
      let deliveries = applied.assessmentDeliveries;
      const removedCourses = [];
      const removedDeliveries = [];

      if (groupType === "custom_course") {
        courses = courses.filter((id) => id !== groupId);
        removedCourses.push(groupId);
      } else if (groupType === "assessment_delivery") {
        deliveries = deliveries.filter((id) => id !== groupId);
        removedDeliveries.push(groupId);
      } else {
        return;
      }

      if (this.hasCustomCourseIdsFieldTarget) {
        this.customCourseIdsFieldTarget.value = courses.join(",");
      }
      if (this.hasAssessmentDeliveryIdsFieldTarget) {
        this.assessmentDeliveryIdsFieldTarget.value = deliveries.join(",");
      }

      const reconciled = await this.reconcileStudentsAfterGroupFilterRemoval(
        removedCourses,
        removedDeliveries,
        courses,
        deliveries,
      );

      if (!reconciled) {
        this.restoreGroupFilterRemovalState(snapshot);
        this.showGroupError(POPULATE_ERROR_MESSAGE);
        return;
      }

      this.writeGroupFilterValue("customCoursesDropdown", courses);
      this.writeGroupFilterValue("deliveriesDropdown", deliveries);

      this.trackedGroupFilterCourseIds = courses;
      this.trackedGroupFilterDeliveryIds = deliveries;

      this.updateSelectedGroupsPills({ fromApply: true });

      this.refreshGroupFilterOptionsFrame();
      this.focusAfterGroupPillRemove(removedPillIndex);
    } finally {
      this.groupFilterRemoveInFlight = false;
    }
  }

  snapshotGroupFilterRemovalState() {
    return {
      courses: this.hasCustomCourseIdsFieldTarget
        ? this.customCourseIdsFieldTarget.value
        : "",
      deliveries: this.hasAssessmentDeliveryIdsFieldTarget
        ? this.assessmentDeliveryIdsFieldTarget.value
        : "",
      selectedIds: new Set(this.selectedIds),
      selectedStudents: new Map(this.selectedStudents),
      trackedCourses: [...this.trackedGroupFilterCourseIds],
      trackedDeliveries: [...this.trackedGroupFilterDeliveryIds],
    };
  }

  restoreGroupFilterRemovalState(snapshot) {
    if (this.hasCustomCourseIdsFieldTarget) {
      this.customCourseIdsFieldTarget.value = snapshot.courses;
    }
    if (this.hasAssessmentDeliveryIdsFieldTarget) {
      this.assessmentDeliveryIdsFieldTarget.value = snapshot.deliveries;
    }

    this.selectedIds = new Set(snapshot.selectedIds);
    this.selectedStudents = new Map(snapshot.selectedStudents);
    this.trackedGroupFilterCourseIds = [...snapshot.trackedCourses];
    this.trackedGroupFilterDeliveryIds = [...snapshot.trackedDeliveries];

    this.updateSelectedIdsField();
    this.syncCheckboxesFromSelection();
    this.syncSearchSelectedIdsField();
    this.updateSelectAllState();
    this.updateSelectedList();
  }

  groupPillRemoveButtons() {
    if (!this.hasSelectedGroupsTarget) return [];

    const list = this.selectedGroupsTarget.querySelector(
      ".students-modal__selected-groups-list",
    );
    if (!list) return [];

    return Array.from(
      list.querySelectorAll(".students-modal__group-pill-remove"),
    );
  }

  focusAfterGroupPillRemove(removedPillIndex) {
    const removeButtons = this.groupPillRemoveButtons();
    const nextRemove =
      removedPillIndex >= 0
        ? removeButtons[removedPillIndex] ||
          removeButtons[removedPillIndex - 1] ||
          removeButtons[0]
        : removeButtons[0];

    if (nextRemove) {
      nextRemove.focus();
      return;
    }

    if (!this.hasSelectedGroupsTarget) return;

    const emailRemove = this.selectedGroupsTarget.querySelector(
      ".students-modal__email-pill-remove",
    );
    if (emailRemove) {
      emailRemove.focus();
      return;
    }

    const container = this.selectedGroupsTarget;
    if (!container.hasAttribute("tabindex"))
      container.setAttribute("tabindex", "-1");
    container.focus();
  }

  refreshGroupFilterOptionsFrame() {
    const frame = document.getElementById(GROUP_FILTER_OPTIONS_FRAME_ID);
    if (!frame) return;

    const src = frame.getAttribute("src") || frame.src;
    if (!src) return;

    const current = new URL(src, window.location.origin);
    const next = new URL(src, window.location.origin);

    if (this.hasCustomCourseIdsFieldTarget) {
      const courses = this.customCourseIdsFieldTarget.value.trim();
      if (courses) next.searchParams.set("selected_custom_course_ids", courses);
      else next.searchParams.delete("selected_custom_course_ids");
    }

    if (this.hasAssessmentDeliveryIdsFieldTarget) {
      const deliveries = this.assessmentDeliveryIdsFieldTarget.value.trim();
      if (deliveries)
        next.searchParams.set("selected_assessment_delivery_ids", deliveries);
      else next.searchParams.delete("selected_assessment_delivery_ids");
    }

    const currentPath = `${current.pathname}${current.search}`;
    const nextPath = `${next.pathname}${next.search}`;
    // Re-assigning the same src forces a Turbo Frame reload (visible flicker).
    if (nextPath === currentPath) return;

    frame.src = nextPath;
  }

  readAppliedGroupFilterValues() {
    const fromFields = {
      customCourses: this.hasCustomCourseIdsFieldTarget
        ? this.customCourseIdsFieldTarget.value
            .split(",")
            .map((value) => value.trim())
            .filter(Boolean)
        : [],
      assessmentDeliveries: this.hasAssessmentDeliveryIdsFieldTarget
        ? this.assessmentDeliveryIdsFieldTarget.value
            .split(",")
            .map((value) => value.trim())
            .filter(Boolean)
        : [],
    };

    if (
      fromFields.customCourses.length > 0 ||
      fromFields.assessmentDeliveries.length > 0
    ) {
      return fromFields;
    }

    return this.readGroupFilterValues();
  }

  restoreGroupFilterDropdownFromTracked() {
    this.writeGroupFilterValue(
      "customCoursesDropdown",
      this.trackedGroupFilterCourseIds,
    );
    this.writeGroupFilterValue(
      "deliveriesDropdown",
      this.trackedGroupFilterDeliveryIds,
    );
  }

  writeGroupFilterValue(targetName, values) {
    const wrapper = this.nestedField(targetName);
    if (!wrapper) return;

    const uniqueValues = [...new Set(values.map(String))];
    const dropdownElement = wrapper.matches(
      "[data-controller~='railsui-dropdown-menu']",
    )
      ? wrapper
      : wrapper.closest("[data-controller~='railsui-dropdown-menu']") ||
        wrapper.querySelector("[data-controller~='railsui-dropdown-menu']");

    const dropdownController = dropdownElement
      ? this.application.getControllerForElementAndIdentifier(
          dropdownElement,
          "railsui-dropdown-menu",
        )
      : null;

    if (dropdownController?.uncheckValue) {
      const current = this.readDropdownValues(wrapper);
      current
        .filter((value) => !uniqueValues.includes(value))
        .forEach((value) => {
          dropdownController.uncheckValue(value, { emitChange: false });
        });
      uniqueValues
        .filter((value) => !current.includes(value))
        .forEach((value) => {
          dropdownController.checkValue?.(value, { emitChange: false });
        });
      return;
    }

    const hidden = wrapper.querySelector(
      "[data-railsui-dropdown-menu-target='hiddenInput']",
    );
    if (hidden) hidden.value = uniqueValues.join(",");

    const selectedList = wrapper.querySelector(".dropdown-selected-list");
    if (!selectedList) return;

    Array.from(
      selectedList.querySelectorAll(".dropdown-selected-list-item"),
    ).forEach((item) => {
      if (!uniqueValues.includes(String(item.dataset.value || "")))
        item.remove();
    });
  }

  readGroupFilterItems() {
    const items = [];

    const courseWrapper = this.nestedField("customCoursesDropdown");
    if (courseWrapper) {
      courseWrapper
        .querySelectorAll(".dropdown-selected-list-item")
        .forEach((item) => {
          const label = item
            .querySelector(".dropdown-selected-list-label")
            ?.textContent.trim();
          const value = String(item.dataset.value || "").trim();
          if (label && value) {
            items.push({ label, value, type: "custom_course", tone: "blue" });
          }
        });
    }

    const deliveryWrapper = this.nestedField("deliveriesDropdown");
    if (deliveryWrapper) {
      deliveryWrapper
        .querySelectorAll(".dropdown-selected-list-item")
        .forEach((item) => {
          const label = item
            .querySelector(".dropdown-selected-list-label")
            ?.textContent.trim();
          const value = String(item.dataset.value || "").trim();
          if (label && value) {
            items.push({
              label,
              value,
              type: "assessment_delivery",
              tone: "green",
            });
          }
        });
    }

    return items;
  }

  hasStoredGroupFilterValues() {
    const courseIds = this.hasCustomCourseIdsFieldTarget
      ? this.customCourseIdsFieldTarget.value
      : "";
    const deliveryIds = this.hasAssessmentDeliveryIdsFieldTarget
      ? this.assessmentDeliveryIdsFieldTarget.value
      : "";

    return Boolean(courseIds.trim() || deliveryIds.trim());
  }

  buildSelectedListItem(student) {
    if (this.hasSelectedListItemTemplateTarget) {
      const item =
        this.selectedListItemTemplateTarget.content.firstElementChild.cloneNode(
          true,
        );
      item.dataset.studentId = student.id;
      delete item.dataset.email;
      const label = item.querySelector(".dropdown-selected-list-label");
      if (label) {
        label.textContent = student.email;
        label.classList.remove("students-modal__selected-email--unknown");
        label.removeAttribute("data-controller");
        label.removeAttribute("data-railsui-tooltip-content-value");
      }
      const button = item.querySelector(".dropdown-selected-list-remove");
      if (button) {
        button.dataset.studentId = student.id;
        delete button.dataset.email;
        button.dataset.action = "click->students-modal#removeSelectedStudent";
        button.setAttribute("aria-label", `Remove ${student.email}`);
      }
      return item;
    }

    const item = document.createElement("li");
    item.className = "dropdown-selected-list-item";
    item.dataset.studentsModalTarget = "selectedListItem";
    item.dataset.studentId = student.id;

    const label = document.createElement("span");
    label.className = "dropdown-selected-list-label";
    label.textContent = student.email;

    const button = document.createElement("button");
    button.type = "button";
    button.className = "dropdown-selected-list-remove";
    button.dataset.action = "click->students-modal#removeSelectedStudent";
    button.dataset.studentId = student.id;
    button.setAttribute("aria-label", `Remove ${student.email}`);

    item.append(label, button);
    return item;
  }

  buildSelectedUnknownListItem(email) {
    const item = this.hasSelectedListItemTemplateTarget
      ? this.selectedListItemTemplateTarget.content.firstElementChild.cloneNode(
          true,
        )
      : document.createElement("li");

    if (!this.hasSelectedListItemTemplateTarget) {
      item.className = "dropdown-selected-list-item";
      const label = document.createElement("span");
      label.className = "dropdown-selected-list-label";
      const button = document.createElement("button");
      button.type = "button";
      button.className = "dropdown-selected-list-remove";
      item.append(label, button);
    }

    delete item.dataset.studentId;
    item.dataset.email = email;
    item.dataset.studentsModalEmailUnknown = "true";

    let label = item.querySelector(".dropdown-selected-list-label");
    if (!label) {
      label = document.createElement("span");
      label.className = "dropdown-selected-list-label";
      item.prepend(label);
    }
    label.textContent = email;
    label.classList.add("students-modal__selected-email--unknown");
    label.dataset.controller = "railsui-tooltip";
    label.dataset.railsuiTooltipContentValue = UNKNOWN_EMAILS_MESSAGE;

    const button = item.querySelector(".dropdown-selected-list-remove");
    if (button) {
      delete button.dataset.studentId;
      button.dataset.email = email;
      button.dataset.action = "click->students-modal#removeSelectedEmail";
      button.setAttribute("aria-label", `Remove ${email}`);
    }

    return item;
  }

  rememberStudent(grdu) {
    const id = String(grdu.id);
    this.selectedIds.add(id);
    this.selectedStudents.set(id, {
      id,
      email: grdu.email || "",
      firstName: grdu.first_name || grdu.firstName || "",
      lastName: grdu.last_name || grdu.lastName || "",
      userId: grdu.user_id || grdu.userId || "",
    });
  }

  rememberStudentFromRow(row) {
    const id = String(row.dataset.studentId);
    const { firstName, lastName } = this.rowName(row);
    this.rememberStudent({
      id,
      email: this.rowEmail(row),
      first_name: firstName,
      last_name: lastName,
      user_id: row.dataset.userId || "",
    });
  }

  rowEmail(row) {
    const emailCell = row.querySelector(".students-modal__email-cell");
    if (emailCell) return emailCell.textContent.trim();

    const emailValue = row.querySelector("[data-students-modal-email]");
    if (emailValue) return emailValue.textContent.trim();

    const values = row.querySelectorAll(".students-modal__mobile-card-value");
    if (values.length > 0) return values[0].textContent.trim();

    return "";
  }

  rowName(row) {
    const lastNameCell = row.querySelector(".students-modal__last-name-cell");
    const firstNameCell = row.querySelector(".students-modal__first-name-cell");
    if (lastNameCell || firstNameCell) {
      return {
        firstName: firstNameCell ? firstNameCell.textContent.trim() : "",
        lastName: lastNameCell ? lastNameCell.textContent.trim() : "",
      };
    }

    const studentNameCell = row.querySelector(
      ".students-modal__student-name-cell",
    );
    if (studentNameCell) {
      return this.splitFullName(studentNameCell.textContent.trim());
    }

    const values = row.querySelectorAll(".students-modal__mobile-card-value");
    if (values.length >= 2)
      return this.splitFullName(values[1].textContent.trim());

    const cells = row.querySelectorAll("td");
    if (cells.length >= 3)
      return this.splitFullName(cells[2].textContent.trim());

    return { firstName: "", lastName: "" };
  }

  splitFullName(fullName) {
    const parts = fullName.split(/\s+/).filter(Boolean);
    if (parts.length === 0) return { firstName: "", lastName: "" };
    if (parts.length === 1) return { firstName: parts[0], lastName: "" };

    return { firstName: parts[0], lastName: parts.slice(1).join(" ") };
  }

  hydrateSelectedStudentsFromList() {
    if (!this.hasSelectedListItemTarget) return;

    this.selectedListItemTargets.forEach((item) => {
      const id = String(item.dataset.studentId);
      const label = item.querySelector(".dropdown-selected-list-label");
      if (!id || !label || this.selectedStudents.has(id)) return;
      this.selectedStudents.set(id, {
        id,
        email: label.textContent.trim(),
        firstName: "",
        lastName: "",
        userId: item.dataset.userId || "",
      });
    });

    this.initialStudentUserIds = new Map();
    this.selectedStudents.forEach((student, id) => {
      if (this.initialSelectedIds.has(id) && student.userId) {
        this.initialStudentUserIds.set(id, student.userId);
      }
    });
  }

  visibleStudentRows() {
    return this.studentRowTargets.filter((row) => !row.hidden);
  }

  resetPageField() {
    if (!this.hasPageFieldTarget) return;
    this.pageFieldTarget.value = "1";
  }

  clearSearchTimer() {
    if (this.searchTimer) {
      window.clearTimeout(this.searchTimer);
      this.searchTimer = null;
    }
  }

  showGroupError(message) {
    const errorEl = this.nestedField("groupError");
    if (!errorEl) return;
    errorEl.textContent = message;
    errorEl.hidden = false;
  }

  clearGroupError() {
    const errorEl = this.nestedField("groupError");
    if (!errorEl) return;
    errorEl.textContent = "";
    errorEl.hidden = true;
  }

  showUnknownEmails(unknown) {
    const unknownEmails = this.nestedField("unknownEmails");
    if (!unknownEmails) return;

    const list = this.nestedField("unknownEmailsList");
    const emailInput = this.nestedField("emailInput");
    const emails = Array.from(unknown || []).map((email) =>
      String(email).toLowerCase(),
    );

    if (list) {
      list.textContent = emails.length
        ? `Unknown emails: ${emails.join(", ")}`
        : "";
    }

    unknownEmails.hidden = emails.length === 0;
    emailInput?.classList.toggle("is-invalid", emails.length > 0);

    if (emails.length === 0) {
      this.setEmailApplyRegisterAnyway(false);
    }
  }

  clearUnknownEmailsBanner() {
    const unknownEmails = this.nestedField("unknownEmails");
    const list = this.nestedField("unknownEmailsList");
    const emailInput = this.nestedField("emailInput");
    if (list) list.textContent = "";
    if (unknownEmails) unknownEmails.hidden = true;
    emailInput?.classList.remove("is-invalid");
  }

  showEmailError(message) {
    const unknownEmails = this.nestedField("unknownEmails");
    if (!unknownEmails) return;
    const list = this.nestedField("unknownEmailsList");
    if (list) list.textContent = message;
    unknownEmails.hidden = false;
    this.nestedField("emailInput")?.classList.add("is-invalid");
  }

  setEmailApplyRegisterAnyway(enabled) {
    const button = this.nestedField("emailApplyButton");
    if (!button) return;

    const defaultText =
      button.dataset.defaultText || button.textContent.trim() || "Select Students";
    const registerText =
      button.dataset.registerAnywayText || REGISTER_ANYWAY_TEXT;
    const label = enabled ? registerText : defaultText;

    button.textContent = label;
    button.setAttribute("aria-label", label);
    button.classList.toggle("btn-critical", enabled);
    button.classList.toggle("btn-primary", !enabled);
  }

  readDropdownValues(wrapper) {
    if (!wrapper) return [];

    const hidden = wrapper.querySelector(
      "[data-railsui-dropdown-menu-target='hiddenInput']",
    );
    return (hidden?.value || "")
      .split(",")
      .map((value) => value.trim())
      .filter(Boolean);
  }

  readGroupFilterValues() {
    return {
      customCourses: this.readDropdownValues(
        this.nestedField("customCoursesDropdown"),
      ),
      assessmentDeliveries: this.readDropdownValues(
        this.nestedField("deliveriesDropdown"),
      ),
    };
  }

  syncTrackedGroupFilterValues() {
    const { customCourses, assessmentDeliveries } =
      this.readGroupFilterValues();
    this.trackedGroupFilterCourseIds = customCourses;
    this.trackedGroupFilterDeliveryIds = assessmentDeliveries;
  }

  async fetchPopulateGrdus(filterData) {
    if (!this.populateUrlValue) return [];

    const response = await fetch(this.populateUrlValue, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
        "X-CSRF-Token": this.csrfToken(),
      },
      body: JSON.stringify({ filterData }),
    });

    if (!response.ok) return null;

    try {
      const data = await response.json();
      return data.grdus || [];
    } catch (_error) {
      return null;
    }
  }

  readStoredGroupFilterValues() {
    return {
      customCourses: this.hasCustomCourseIdsFieldTarget
        ? this.customCourseIdsFieldTarget.value
            .split(",")
            .map((value) => value.trim())
            .filter(Boolean)
        : [],
      assessmentDeliveries: this.hasAssessmentDeliveryIdsFieldTarget
        ? this.assessmentDeliveryIdsFieldTarget.value
            .split(",")
            .map((value) => value.trim())
            .filter(Boolean)
        : [],
    };
  }

  hasAppliedGroupPillSelections() {
    if (!this.hasSelectedGroupsTarget) return false;

    return Boolean(
      this.selectedGroupsTarget.querySelector(".students-modal__group-pill"),
    );
  }

  async reconcileGroupFiltersAfterStudentRemoval() {
    if (this.groupFilterRemoveInFlight) return true;
    if (!this.hasStoredGroupFilterValues()) return true;
    if (!this.hasAppliedGroupPillSelections()) return true;

    const applied = this.readStoredGroupFilterValues();
    let courses = [...applied.customCourses];
    let deliveries = [...applied.assessmentDeliveries];

    if (courses.length === 0 && deliveries.length === 0) {
      this.updateSelectedGroupsPills({ fromApply: true });
      return true;
    }

    const remainingIds = this.selectedIds;
    const results = await Promise.all([
      ...courses.map(async (courseId) => ({
        type: "custom_course",
        id: courseId,
        grdus: await this.fetchPopulateGrdus({
          custom_courses: [courseId],
          assessment_deliveries: [],
        }),
      })),
      ...deliveries.map(async (deliveryId) => ({
        type: "assessment_delivery",
        id: deliveryId,
        grdus: await this.fetchPopulateGrdus({
          custom_courses: [],
          assessment_deliveries: [deliveryId],
        }),
      })),
    ]);

    if (results.some(({ grdus }) => grdus === null)) return false;

    const removedCourses = [];
    const removedDeliveries = [];

    results.forEach(({ type, id, grdus }) => {
      const hasRemaining = grdus.some((grdu) =>
        remainingIds.has(String(grdu.id)),
      );
      if (hasRemaining) return;

      if (type === "custom_course") removedCourses.push(id);
      else removedDeliveries.push(id);
    });

    if (removedCourses.length === 0 && removedDeliveries.length === 0) {
      this.updateSelectedGroupsPills({ fromApply: true });
      return true;
    }

    courses = courses.filter((id) => !removedCourses.includes(id));
    deliveries = deliveries.filter((id) => !removedDeliveries.includes(id));

    if (this.hasCustomCourseIdsFieldTarget) {
      this.customCourseIdsFieldTarget.value = courses.join(",");
    }
    if (this.hasAssessmentDeliveryIdsFieldTarget) {
      this.assessmentDeliveryIdsFieldTarget.value = deliveries.join(",");
    }

    this.trackedGroupFilterCourseIds = courses;
    this.trackedGroupFilterDeliveryIds = deliveries;
    this.writeGroupFilterValue("customCoursesDropdown", courses);
    this.writeGroupFilterValue("deliveriesDropdown", deliveries);
    this.updateSelectedGroupsPills({ fromApply: true });
    this.refreshGroupFilterOptionsFrame();
    return true;
  }

  async reconcileStudentsAfterGroupFilterRemoval(
    removedCourses,
    removedDeliveries,
    currentCourses,
    currentDeliveries,
  ) {
    const stillCoveredIds = new Set();

    if (currentCourses.length > 0 || currentDeliveries.length > 0) {
      const grdus = await this.fetchPopulateGrdus({
        custom_courses: currentCourses,
        assessment_deliveries: currentDeliveries,
      });
      if (grdus === null) return false;

      grdus.forEach((grdu) => stillCoveredIds.add(String(grdu.id)));
    }

    const removalFetches = [
      ...removedCourses.map((courseId) =>
        this.fetchPopulateGrdus({
          custom_courses: [courseId],
          assessment_deliveries: [],
        }),
      ),
      ...removedDeliveries.map((deliveryId) =>
        this.fetchPopulateGrdus({
          custom_courses: [],
          assessment_deliveries: [deliveryId],
        }),
      ),
    ];

    if (removalFetches.length > 0) {
      const removalResults = await Promise.all(removalFetches);
      if (removalResults.some((grdus) => grdus === null)) return false;

      removalResults.forEach((grdus) => {
        grdus.forEach((grdu) => {
          const id = String(grdu.id);
          if (stillCoveredIds.has(id)) return;

          this.selectedIds.delete(id);
          this.selectedStudents.delete(id);
        });
      });
    }

    this.updateSelectedIdsField();
    this.syncCheckboxesFromSelection();
    this.syncSearchSelectedIdsField();
    this.updateSelectAllState();
    this.updateSelectedList();
    return true;
  }

  wireGroupFilterDropdownListeners(modal) {
    if (!modal || modal.id !== this.groupModalIdValue) return;

    const signal = this.portaledActionAbort?.signal;

    modal.addEventListener(
      "change",
      (event) => {
        const input = event.target;
        if (
          !input?.matches?.("[data-railsui-dropdown-menu-target='hiddenInput']")
        )
          return;

        const inGroupDropdown = input.closest(
          "[data-students-modal-target='customCoursesDropdown'], [data-students-modal-target='deliveriesDropdown']",
        );
        if (!inGroupDropdown) return;

        void this.groupFilterDropdownChanged();
      },
      { signal },
    );

    modal.addEventListener(
      "turbo:frame-load",
      (event) => {
        const frame = event.target;
        if (frame.id !== GROUP_FILTER_OPTIONS_FRAME_ID) return;

        this.restoreGroupFilterDropdownFromTracked();
      },
      { signal },
    );
  }

  nestedField(targetName) {
    const selector = `[data-students-modal-target='${targetName}']`;
    return (
      this.groupModalRoot()?.querySelector(selector) ||
      this.emailModalRoot()?.querySelector(selector) ||
      null
    );
  }

  groupModalRoot() {
    if (!this.groupModalIdValue) return null;
    return document.getElementById(this.groupModalIdValue);
  }

  emailModalRoot() {
    if (!this.emailModalIdValue) return null;
    return document.getElementById(this.emailModalIdValue);
  }

  portalNestedModals() {
    [this.groupModalRoot(), this.emailModalRoot()]
      .filter(Boolean)
      .forEach((modal) => {
        if (modal.dataset.portaled === "true") return;
        if (modal.parentElement === document.body) {
          modal.dataset.portaled = "true";
          this.wirePortaledModalActions(modal);
          return;
        }

        const home = document.createComment(`students-modal-${modal.id}`);
        modal.before(home);
        document.body.appendChild(modal);
        modal.dataset.portaled = "true";
        this.portalHomes.set(modal, home);
        this.wirePortaledModalActions(modal);
      });
  }

  discardPortaledModals() {
    this.portalHomes.forEach((home, modal) => {
      if (modal.isConnected) {
        closeRailsuiModal(this.application, modal.id, document);
        modal.remove();
      }
      if (home.isConnected) home.remove();
    });
    this.portalHomes.clear();
    [this.groupModalIdValue, this.emailModalIdValue]
      .filter(Boolean)
      .forEach((modalId) => {
        document.querySelectorAll(`#${modalId}`).forEach((modal) => {
          if (
            modal.parentElement !== document.body ||
            modal.dataset.portaled !== "true"
          )
            return;

          closeRailsuiModal(this.application, modalId, document);
          modal.remove();
        });
      });
  }

  wirePortaledModalActions(modal) {
    const signal = this.portaledActionAbort?.signal;
    const actionPattern = /students-modal#(\w+)/;

    modal.querySelectorAll("[data-action]").forEach((element) => {
      const match = (element.dataset.action || "").match(actionPattern);
      if (!match) return;

      const method = this[match[1]];
      if (typeof method !== "function") return;

      element.addEventListener("click", (event) => method.call(this, event), {
        signal,
      });
    });

    this.wireGroupFilterDropdownListeners(modal);
    this.wireEmailApplyButton(modal, signal);
  }

  wireEmailApplyButton(modal, signal) {
    const emailInput = modal.querySelector(
      "[data-students-modal-target='emailInput']",
    );
    if (!emailInput) return;

    emailInput.addEventListener(
      "input",
      () => {
        this.syncEmailApplyButtonState();
      },
      { signal },
    );
    this.syncEmailApplyButtonState();
  }

  restoreNestedModals() {
    this.portalHomes.forEach((home, modal) => {
      if (home.isConnected && modal.isConnected) {
        closeRailsuiModal(this.application, modal.id, document);
        home.replaceWith(modal);
        delete modal.dataset.portaled;
        return;
      }

      if (modal.isConnected && modal.parentElement === document.body) {
        closeRailsuiModal(this.application, modal.id, document);
        modal.remove();
      }
      if (home.isConnected) home.remove();
    });
    this.portalHomes.clear();
  }

  replaceTargetId() {
    return this.replaceTargetIdValue || this.element.id;
  }

  csrfToken() {
    const meta = document.querySelector("meta[name='csrf-token']");
    return meta ? meta.getAttribute("content") : "";
  }
}
